import { Card, Button, Select, Typography, Spin, Avatar, Row, Col, Statistic, Tooltip, Modal, Form, Input, message, Alert } from 'antd';
import { ArrowLeftOutlined, EditOutlined, MailOutlined, PhoneOutlined, IdcardOutlined, CalendarOutlined, ClockCircleOutlined, BookOutlined, PlayCircleOutlined, FileDoneOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { approveTeacher } from '@/services/onboarding.service';
import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { ROLE_STYLES, USER_STATUSES } from '@/constants';
import { UserDetail, UserStatusValue } from '@/types';

interface EditUserFormValues {
  name: string;
  email: string;
  phone?: string;
  status: UserStatusValue;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatRelative(value: string | null | undefined): string {
  if (!value) return 'Never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Never';
  const diffMs = Date.now() - d.getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} mo ago`;
  const years = Math.floor(months / 12);
  return `${years} yr${years === 1 ? '' : 's'} ago`;
}

function StatusPill({ status }: { status: UserStatusValue }) {
  const color = status === 'ACTIVE' ? '#065f46' : status === 'SUSPENDED' ? '#991b1b' : '#6b7280';
  const bg = status === 'ACTIVE' ? '#d1fae5' : status === 'SUSPENDED' ? '#fee2e2' : '#f3f4f6';
  const dot = status === 'ACTIVE' ? '#10b981' : status === 'SUSPENDED' ? '#ef4444' : '#9ca3af';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, color, background: bg }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot }} />
      {status}
    </span>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: '#eef2ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 15 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 500, wordBreak: 'break-word' }}>{value}</div>
      </div>
    </div>
  );
}

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm<EditUserFormValues>();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['user', id],
    queryFn: () => api.get<UserDetail>(`/admin/users/${id}`),
    enabled: !!id,
  });

  const updateStatus = useMutation({
    mutationFn: (status: UserStatusValue) => api.patch<{ data: UserDetail }>(`/admin/users/${id}`, { status }),
    onSuccess: () => {
      message.success('Status updated');
      qc.invalidateQueries({ queryKey: ['user', id] });
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['teachers'] });
      qc.invalidateQueries({ queryKey: ['students'] });
    },
    onError: () => message.error('Failed to update status'),
  });

  const updateUser = useMutation({
    mutationFn: (values: EditUserFormValues) => api.patch(`/admin/users/${id}`, values),
    onSuccess: () => {
      message.success('User updated successfully');
      qc.invalidateQueries({ queryKey: ['user', id] });
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['teachers'] });
      qc.invalidateQueries({ queryKey: ['students'] });
      setEditOpen(false);
      editForm.resetFields();
    },
    onError: (err: any) => {
      const msg = err?.error?.message ?? err?.message ?? 'Failed to update user';
      message.error(msg);
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => approveTeacher(id!),
    onSuccess: () => {
      message.success('Teacher approved — they can now publish decks');
      qc.invalidateQueries({ queryKey: ['user', id] });
      qc.invalidateQueries({ queryKey: ['pending-teachers'] });
    },
    onError: () => message.error('Failed to approve teacher'),
  });

  const closeEdit = useCallback(() => {
    setEditOpen(false);
    editForm.resetFields();
  }, [editForm]);

  const user = data?.data;

  if (isLoading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <Spin size="large" />
    </div>
  );

  if (isError || !user) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, flexDirection: 'column' }}>
        <Typography.Text style={{ color: '#ef4444', marginBottom: 16 }}>Failed to load user details</Typography.Text>
        <Button onClick={() => qc.invalidateQueries({ queryKey: ['user', id] })}>Retry</Button>
      </div>
    );
  }

  const role = user.role;
  const roleStyle = ROLE_STYLES[role ?? ''] ?? { color: '#374151', bg: '#f3f4f6' };
  const counts = user._count ?? { decks: 0, sessions: 0, assignments: 0 };
  const isStudent = role === 'STUDENT';
  const isTeacher = role === 'TEACHER';

  return (
    <div>
      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back</Button>
          <div>
            <Typography.Title level={4} style={{ margin: 0, color: '#0f172a' }}>User Profile</Typography.Title>
            <Typography.Text style={{ color: '#64748b', fontSize: 13 }}>View and manage user account</Typography.Text>
          </div>
        </div>
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => setEditOpen(true)}
          style={{ background: '#4f46e5', borderColor: '#4f46e5' }}
        >
          Edit User
        </Button>
      </div>

      <Row gutter={[16, 16]}>
        {/* Left: Identity card */}
        <Col xs={24} lg={8}>
          <Card style={{ border: '1px solid #e2e8f0', borderRadius: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '8px 0' }}>
              <Avatar
                size={88}
                style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', fontWeight: 700, fontSize: 34, marginBottom: 16 }}
              >
                {user.name?.[0]?.toUpperCase()}
              </Avatar>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>{user.name}</div>
              <span style={{
                display: 'inline-block',
                padding: '3px 12px',
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 700,
                color: roleStyle.color,
                background: roleStyle.bg,
                marginBottom: 12,
                letterSpacing: '0.3px',
              }}>
                {role}
              </span>
              <div style={{ marginBottom: 8 }}>
                <StatusPill status={user.status as UserStatusValue} />
              </div>
              {isTeacher && user.teacherStatus && (
                <div style={{ marginBottom: 16 }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    borderRadius: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    color: user.teacherStatus === 'ACTIVE' ? '#065f46' : user.teacherStatus === 'PENDING_APPROVAL' ? '#92400e' : '#991b1b',
                    background: user.teacherStatus === 'ACTIVE' ? '#d1fae5' : user.teacherStatus === 'PENDING_APPROVAL' ? '#fef3c7' : '#fee2e2',
                  }}>
                    {user.teacherStatus === 'ACTIVE' ? 'Approved' : user.teacherStatus === 'PENDING_APPROVAL' ? 'Pending Approval' : user.teacherStatus}
                  </span>
                </div>
              )}
              <div style={{ width: '100%', borderTop: '1px solid #f1f5f9', paddingTop: 16, marginTop: 4 }}>
                <Typography.Text style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 8 }}>
                  Change account status
                </Typography.Text>
                <Select
                  value={String(user.status)}
                  style={{ width: '100%' }}
                  onChange={(v) => updateStatus.mutate(v as UserStatusValue)}
                  loading={updateStatus.isPending}
                  options={Object.values(USER_STATUSES).map((status) => ({
                    value: status,
                    label: status.charAt(0) + status.slice(1).toLowerCase(),
                  }))}
                />
              </div>
            </div>
          </Card>
        </Col>

        {/* Right: Details + Stats */}
        <Col xs={24} lg={16}>
          {isTeacher && user.teacherStatus === 'PENDING_APPROVAL' && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16, borderRadius: 10 }}
              message="Teacher pending approval"
              description="This teacher cannot publish decks until approved. Approve their account to allow them to publish content."
              action={
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={approveMutation.isPending}
                  onClick={() => approveMutation.mutate()}
                  style={{ background: '#059669', borderColor: '#059669' }}
                >
                  Approve Teacher
                </Button>
              }
            />
          )}
          {/* Stats row */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {isTeacher && (
              <Col xs={24} sm={8}>
                <Card style={{ border: '1px solid #e2e8f0', borderRadius: 12 }} styles={{ body: { padding: 16 } }}>
                  <Statistic
                    title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>DECKS CREATED</span>}
                    value={counts.decks ?? 0}
                    prefix={<BookOutlined style={{ color: '#4f46e5' }} />}
                    valueStyle={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}
                  />
                </Card>
              </Col>
            )}
            {isStudent && (
              <Col xs={24} sm={8}>
                <Card style={{ border: '1px solid #e2e8f0', borderRadius: 12 }} styles={{ body: { padding: 16 } }}>
                  <Statistic
                    title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>ASSIGNMENTS</span>}
                    value={counts.assignments ?? 0}
                    prefix={<FileDoneOutlined style={{ color: '#059669' }} />}
                    valueStyle={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}
                  />
                </Card>
              </Col>
            )}
            <Col xs={24} sm={8}>
              <Card style={{ border: '1px solid #e2e8f0', borderRadius: 12 }} styles={{ body: { padding: 16 } }}>
                <Statistic
                  title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>SESSIONS</span>}
                  value={counts.sessions ?? 0}
                  prefix={<PlayCircleOutlined style={{ color: '#d97706' }} />}
                  valueStyle={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card style={{ border: '1px solid #e2e8f0', borderRadius: 12 }} styles={{ body: { padding: 16 } }}>
                <Statistic
                  title={<span style={{ color: '#64748b', fontSize: 12, fontWeight: 600 }}>LAST ACTIVE</span>}
                  value={formatRelative(user.lastActiveAt)}
                  prefix={<ClockCircleOutlined style={{ color: '#0891b2' }} />}
                  valueStyle={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Contact details */}
          <Card
            title={<span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Account Information</span>}
            style={{ border: '1px solid #e2e8f0', borderRadius: 12 }}
            styles={{ header: { borderBottom: '1px solid #f1f5f9', padding: '12px 20px', minHeight: 'auto' }, body: { padding: '8px 20px 16px' } }}
          >
            <InfoRow
              icon={<MailOutlined />}
              label="Email"
              value={user.email || '—'}
            />
            <InfoRow
              icon={<PhoneOutlined />}
              label="Phone"
              value={user.phone || '—'}
            />
            <InfoRow
              icon={<IdcardOutlined />}
              label="User ID"
              value={
                <Tooltip title="Click to copy">
                  <code
                    onClick={() => {
                      navigator.clipboard?.writeText(user.id);
                      message.success('User ID copied');
                    }}
                    style={{ fontSize: 11, background: '#f8fafc', padding: '3px 8px', borderRadius: 4, cursor: 'pointer', color: '#475569' }}
                  >
                    {user.id}
                  </code>
                </Tooltip>
              }
            />
            <InfoRow
              icon={<CalendarOutlined />}
              label="Joined"
              value={formatDate(user.createdAt)}
            />
            <InfoRow
              icon={<ClockCircleOutlined />}
              label="Last Active"
              value={user.lastActiveAt ? `${formatDate(user.lastActiveAt)} (${formatRelative(user.lastActiveAt)})` : 'Never'}
            />
          </Card>
        </Col>
      </Row>

      {/* Edit modal */}
      <Modal
        title="Edit User"
        open={editOpen}
        onCancel={closeEdit}
        onOk={() => editForm.submit()}
        okText="Save changes"
        confirmLoading={updateUser.isPending}
        destroyOnHidden
        maskClosable={false}
      >
        <Form
          form={editForm}
          layout="vertical"
          preserve={false}
          initialValues={{
            name: user.name,
            email: user.email,
            phone: user.phone ?? '',
            status: user.status,
          }}
          onFinish={(values) => {
            const { email: _email, ...rest } = values;
            const payload: EditUserFormValues = {
              ...rest,
              phone: rest.phone?.trim() ? rest.phone.trim() : undefined,
            } as EditUserFormValues;
            updateUser.mutate(payload);
          }}
          style={{ marginTop: 8 }}
        >
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: 'Name is required' }, { min: 2, max: 100 }]}
          >
            <Input placeholder="Enter full name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
          >
            <Input readOnly style={{ background: '#f8fafc', color: '#64748b', cursor: 'default' }} />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ pattern: /^[+]?[0-9\s\-()]{7,20}$/, message: 'Invalid phone number format' }]}
          >
            <Input placeholder="+91 9876543210" />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Status is required' }]}
          >
            <Select
              options={Object.values(USER_STATUSES).map((s) => ({
                value: s,
                label: s.charAt(0) + s.slice(1).toLowerCase(),
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
